import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo, { faqJsonLd, breadcrumbJsonLd, softwareJsonLd } from "@/components/Seo";
import { Check, ArrowRight, Sparkles, ChevronDown } from "lucide-react";

export function Breadcrumbs({ items }) {
    return (
        <nav className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5" data-testid="breadcrumbs" aria-label="Breadcrumb">
            {items.map((it, i) => (
                <span key={it.path} className="flex items-center gap-1.5">
                    {i > 0 && <span className="text-slate-300">/</span>}
                    {i < items.length - 1 ? (
                        <Link to={it.path} className="hover:text-red-600">{it.name}</Link>
                    ) : (
                        <span className="text-slate-700 font-semibold">{it.name}</span>
                    )}
                </span>
            ))}
        </nav>
    );
}

export function FaqBlock({ faq, title = "Häufige Fragen" }) {
    if (!faq?.length) return null;
    return (
        <div className="mt-14">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{title}</h2>
            <div className="mt-5 space-y-2">
                {faq.map((f, i) => (
                    <details key={i} className="group card-elev px-5 py-4" data-testid={`faq-item-${i}`}>
                        <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-slate-800 text-sm sm:text-base">
                            {f.q}
                            <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0 ml-3" />
                        </summary>
                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                    </details>
                ))}
            </div>
        </div>
    );
}

export function CtaBanner({ label = "Jetzt kostenlos Bewerbung erstellen", sub = "Kostenlos registrieren · 1 Bewerbung gratis · Keine Kreditkarte nötig" }) {
    return (
        <div className="mt-14 rounded-3xl p-8 sm:p-10 text-center text-white" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} data-testid="seo-cta-banner">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold">Bereit? Deine Bewerbung wartet.</h2>
            <p className="mt-2 text-white/90 text-sm">{sub}</p>
            <Link to="/registrieren" data-testid="seo-cta-register" className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm bg-white text-red-600 hover:bg-red-50 transition-colors">
                <Sparkles size={15} /> {label}
            </Link>
        </div>
    );
}

export function RelatedLinks({ links, title = "Passende Themen" }) {
    if (!links?.length) return null;
    return (
        <div className="mt-14">
            <h2 className="font-heading text-xl font-bold text-slate-900">{title}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
                {links.map((l) => (
                    <Link key={l.path} to={l.path} className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-700 transition-colors">
                        {l.name}
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default function SeoLanding({ page }) {
    const crumbs = [{ name: "Start", path: "/" }, { name: page.h1, path: `/${page.slug}` }];
    const jsonLd = [breadcrumbJsonLd(crumbs), softwareJsonLd];
    if (page.faq?.length) jsonLd.push(faqJsonLd(page.faq));
    return (
        <PublicLayout>
            <Seo title={page.title} description={page.metaDescription} path={`/${page.slug}`} jsonLd={jsonLd} />
            <section className="max-w-4xl mx-auto px-6 lg:px-8 py-14" data-testid={`landing-${page.slug}`}>
                <Breadcrumbs items={crumbs} />
                {page.eyebrow && <p className="mt-6 text-[11px] font-bold tracking-[0.2em] uppercase text-red-600">{page.eyebrow}</p>}
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mt-2 leading-tight">{page.h1}</h1>
                {page.intro.map((p, i) => (
                    <p key={i} className="mt-4 text-slate-600 leading-relaxed">{p}</p>
                ))}
                <div className="mt-7 flex flex-wrap gap-3">
                    <Link to={page.ctaTo || "/registrieren"} data-testid="landing-cta-primary" className="px-7 py-3.5 rounded-full font-bold text-sm btn-primary-grad inline-flex items-center gap-2">
                        <Sparkles size={15} /> {page.cta}
                    </Link>
                    <Link to={page.b2b ? "/kontakt" : "/preise"} className="px-7 py-3.5 rounded-full font-semibold text-sm bg-white border-2 border-slate-200 hover:border-red-300 text-slate-700 inline-flex items-center gap-2">
                        {page.b2b ? "Kontakt aufnehmen" : "Preise ansehen"} <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="mt-14 grid sm:grid-cols-2 gap-4">
                    {page.benefits.map((b, i) => (
                        <div key={i} className="card-elev p-5">
                            <p className="font-heading font-bold text-slate-900 text-sm inline-flex items-start gap-2">
                                <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {b.t}
                            </p>
                            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{b.d}</p>
                        </div>
                    ))}
                </div>

                {page.steps?.length > 0 && (
                    <div className="mt-14">
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{page.stepsTitle || "So funktioniert's"}</h2>
                        <div className="mt-6 space-y-4">
                            {page.steps.map((s, i) => (
                                <div key={i} className="flex gap-4 items-start">
                                    <span className="h-8 w-8 rounded-full btn-primary-grad text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                                    <div>
                                        <p className="font-semibold text-slate-900 text-sm">{s.t}</p>
                                        <p className="text-sm text-slate-600 mt-0.5">{s.d}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {page.muster && (
                    <div className="mt-14">
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{page.muster.h}</h2>
                        <div className="mt-5 card-elev p-6 sm:p-8 border-l-4 border-red-500">
                            {page.muster.lines.map((l, i) => (
                                <p key={i} className={`text-sm leading-relaxed ${l === "" ? "h-3" : "text-slate-700"}`}>{l}</p>
                            ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-3">{page.muster.note}</p>
                    </div>
                )}

                <FaqBlock faq={page.faq} />
                <CtaBanner label={page.cta} sub={page.b2b ? "Unverbindlich testen · Persönliche Beratung · DSGVO-konform" : undefined} />
                <RelatedLinks links={page.related} />
            </section>
        </PublicLayout>
    );
}
